/* eslint-disable @atlaskit/ui-styling-standard/no-styled */
import type { ReactNode, ComponentType } from 'react';

import { styled as styled2 } from '@compiled/react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled, { css } from 'styled-components';
import { token } from '@atlaskit/tokens';
import { notDraggingMixin } from '@atlassian/jira-common-styles/src/dragging.tsx';
import { contentHorizontalSpacing } from '@atlassian/jira-common-styles/src/main.tsx';

const scrollShadowHiderMixin = css({
	content: "''",
	display: 'block',
	height: '2px',
	position: 'relative',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('elevation.surface'),
	zIndex: 2,
	/* When scrollbars are visible it causes the scrollable element to shift left which means it no longer covers the
    scrollShadowMixin. We use a negative margin here to increase the width of the shadow hider to ensure the shadow
    remains hidden. This must not exceed the padding of CenteredContent to prevent overflow-x issues.  */
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	margin: `0 ${-contentHorizontalSpacing / 2}px`,
});

// TODO: migrate to object syntax. Autofix is available for many cases. Remove the eslint-disable for @atlaskit/design-system/no-styled-tagged-template-expression to check.
// eslint-disable-next-line  @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const Scrollable = styled.div<{
	children?: ReactNode;
}>`
	/* Set display to flex so shadow hiders can correctly align to bottom and top of the container */
	display: flex;
	flex-direction: column;
	height: 100%;

	&::before {
		${scrollShadowHiderMixin};
		flex-shrink: 0;
	}

	&::after {
		${scrollShadowHiderMixin};
		flex-shrink: 0;
		/* Scrollable grows to the height of the parent. So we need to make sure the hider aligns to the bottom */
		margin-top: auto;
	}
`;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const scrollShadowMixin = css({
	content: "''",
	display: 'block',
	height: '2px',
	position: 'relative',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('color.background.input'),
	zIndex: 1,
});

const NonShrinkable = styled.div<{
	children?: ReactNode;
}>({
	flexShrink: 0,
	flexGrow: 0,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const NonShrinkableHeader: ComponentType<{
	children?: ReactNode;
}> =
	// TODO: migrate to object syntax. Autofix is available for many cases. Remove the eslint-disable for @atlaskit/design-system/no-styled-tagged-template-expression to check.
	// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression -- To migrate as part of go/ui-styling-standard
	styled(NonShrinkable)`
		&::after {
			/* Add a shadow after the header which will only appear when Scrollable is scrolled */
			${scrollShadowMixin};
			margin-bottom: ${token('space.negative.025')};
		}
	`;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const NonShrinkableFooter: ComponentType<{
	children?: ReactNode;
}> =
	// TODO: migrate to object syntax. Autofix is available for many cases. Remove the eslint-disable for @atlaskit/design-system/no-styled-tagged-template-expression to check.
	// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression -- To migrate as part of go/ui-styling-standard
	styled(NonShrinkable)`
		&::before {
			/* Add a shadow before the footer which will only appear when Scrollable is scrolled */
			${scrollShadowMixin};
			margin-top: ${token('space.negative.025')};
		}
	`;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles
export const Column = styled2.div<{
	children?: ReactNode;
}>({
	display: 'flex',
	flexDirection: 'column',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& > *': {
		flexShrink: 0,
	},
});

export const LAYOUT_ITEM_HIGHLIGHTED_TRANSITION_PARAMS = '250ms cubic-bezier(0.15, 1, 0.3, 1)';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const elevatedLayoutItemShadowMixin = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	boxShadow: `${token('elevation.shadow.raised')}, ${token('elevation.shadow.raised')}`,
});

// FIXME: type error passing result of `css` to `styled`
// eslint-disable-next-line @atlaskit/design-system/no-css-tagged-template-expression, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const layoutItemNotDraggingMixin = css`
	${notDraggingMixin}
	/**
     * Override to fix invisible right shadow on bigger screens.
     * Even though mixin is used across Jira, decision was to
     * override only in Swift.
     */
     ${elevatedLayoutItemShadowMixin}
`;
